import BackButton from "../components/BackButton";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CheckCircle2, Loader2, RotateCw, Save, X, ZoomIn, ZoomOut } from "lucide-react";
import { api } from "../lib/api";
import { formatDateTime } from "../lib/format";
import ConfidenceBadge from "../components/ConfidenceBadge";
import StatusBadge from "../components/StatusBadge";

export default function Review() {
  const { docId } = useParams();
  const nav = useNavigate();
  const [ext, setExt] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewMime, setPreviewMime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [savingId, setSavingId] = useState(null);
  const [previewError, setPreviewError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, urlRes] = await Promise.all([api.extraction(docId), api.signedUrl(docId)]);
      setExt(e);
      setPreviewUrl(urlRes.url);
    } catch (err) {
      toast.error("Impossibile caricare la revisione");
      nav("/documenti");
    } finally {
      setLoading(false);
    }
  }, [docId, nav]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Detect preview mime from doc list; fall back to fetching one doc
    (async () => {
      try {
        const list = await api.listDocuments();
        const d = list.find((x) => x.id === docId);
        if (d) setPreviewMime(d.mime_type);
      } catch {}
    })();
  }, [docId]);

  if (loading || !ext) {
    return (
      <div className="min-h-[50vh] grid place-items-center opacity-70" data-testid="review-loading">
        <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Caricamento revisione…</div>
      </div>
    );
  }

  const isConfirmed = ext.status === "confirmed";
  const isRejected = ext.status === "rejected";
  const locked = isConfirmed || isRejected;

  const doSave = async (f, newVal) => {
    setSavingId(f.id);
    try {
      await api.editField(ext.id, f.id, newVal, true);
      await load();
      toast.success("Campo aggiornato");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingId(null);
    }
  };

  const confirm = async () => {
    try {
      await api.confirmExtraction(ext.id);
      toast.success("Estrazione confermata");
      await load();
    } catch {
      toast.error("Conferma non riuscita");
    }
  };

  const reject = async () => {
    if (!window.confirm("Rifiutare questa estrazione? Lo stato passerà a 'rifiutato'.")) return;
    try {
      await api.rejectExtraction(ext.id);
      toast("Estrazione rifiutata");
      await load();
    } catch {
      toast.error("Rifiuto non riuscito");
    }
  };

  return (
    <div className="space-y-6" data-testid="review-page">
      <BackButton to="/documenti" testid="review-back-btn2" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/documenti" className="inline-flex items-center gap-1.5 text-xs font-semibold opacity-70 hover:opacity-100" data-testid="review-back-link">
            <ArrowLeft size={14} /> Torna ai documenti
          </Link>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">Revisione estrazione</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs opacity-80">
            <span className="font-mono uppercase tracking-widest">{ext.extraction_type}</span>
            <StatusBadge status={ext.status} testid="review-status-badge" />
            <ConfidenceBadge value={ext.confidence_overall} testid="review-overall-confidence" />
            <span>· aggiornato {formatDateTime(ext.updated_at)}</span>
            <span>· modello {ext.model_used}</span>
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="review-actions">
          <button
            onClick={reject}
            disabled={locked}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border bg-white disabled:opacity-50"
            style={{ borderColor: "var(--pxos-border-strong)", color: "var(--pxos-text-dark)" }}
            data-testid="review-reject-btn"
          >
            <X size={14} /> Rifiuta
          </button>
          <button
            onClick={confirm}
            disabled={locked}
            className="pxos-btn-primary inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
            data-testid="review-confirm-btn"
          >
            <Check size={14} /> Conferma
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Preview */}
        <div className="pxos-card overflow-hidden lg:sticky lg:top-24 h-fit" data-testid="review-preview-panel">
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: "var(--pxos-border)" }}>
            <div className="text-xs font-mono uppercase tracking-widest opacity-70">Anteprima documento</div>
            <div className="flex items-center gap-1">
              <IconBtn onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))} label="Zoom out" testid="preview-zoom-out"><ZoomOut size={14} /></IconBtn>
              <IconBtn onClick={() => setZoom((z) => Math.min(3, z + 0.2))} label="Zoom in" testid="preview-zoom-in"><ZoomIn size={14} /></IconBtn>
              <IconBtn onClick={() => setRotation((r) => (r + 90) % 360)} label="Ruota" testid="preview-rotate"><RotateCw size={14} /></IconBtn>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-auto bg-[color:var(--pxos-bg-subtle)]" data-testid="review-preview-container">
            <div className="grid place-items-center py-4" style={{ minHeight: "60vh" }}>
              {previewError && (
                <div className="text-sm opacity-70 p-6 text-center" data-testid="review-preview-error">
                  Anteprima non disponibile per questo documento.
                </div>
              )}
              {!previewError && previewUrl && (previewMime?.startsWith("image/") ? (
                <img
                  src={previewUrl}
                  alt="preview"
                  onError={() => setPreviewError(true)}
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transformOrigin: "center", transition: "transform 200ms ease" }}
                  className="max-w-full"
                  data-testid="review-preview-image"
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title="preview-pdf"
                  onError={() => setPreviewError(true)}
                  className="w-full"
                  style={{ minHeight: "70vh", border: 0, transform: `rotate(${rotation}deg)` }}
                  data-testid="review-preview-pdf"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="pxos-card overflow-hidden" data-testid="review-fields-panel">
          <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
            <div className="text-xs font-mono uppercase tracking-widest opacity-70">Campi estratti</div>
            <div className="text-xs opacity-70">{ext.fields.length} campi</div>
          </div>
          <ul className="divide-y" style={{ borderColor: "var(--pxos-border)" }} data-testid="fields-list">
            {ext.fields.length === 0 && (
              <li className="p-6 text-center text-sm opacity-70">Nessun campo estratto per questo documento.</li>
            )}
            {ext.fields.map((f) => (
              <FieldRow key={f.id} f={f} locked={locked} saving={savingId === f.id} onSave={(v) => doSave(f, v)} />
            ))}
          </ul>
        </div>
      </div>

      {isConfirmed && (
        <div className="pxos-card px-5 py-4 flex items-center gap-3" data-testid="review-confirmed-note">
          <CheckCircle2 size={16} /> Estrazione confermata — i dati sono considerati validati. Nessun import automatico verso altri moduli.
        </div>
      )}
    </div>
  );
}

function IconBtn({ children, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--pxos-primary-light)]"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function FieldRow({ f, locked, saving, onSave }) {
  const dateStr = f.value_date ? new Date(f.value_date).toLocaleDateString("it-IT") : null;
  const initial = f.value_text ?? (f.value_numeric ?? dateStr ?? "");
  const [value, setValue] = useState(initial);
  const [dirty, setDirty] = useState(false);

  const asPct = f.confidence !== null && f.confidence !== undefined ? Math.round(f.confidence * 100) + "%" : "—";
  const needsReview = f.needs_review;

  return (
    <li className="px-5 py-4" data-testid="field-row" data-field-name={f.field_name} data-needs-review={needsReview}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <div className="text-xs font-mono uppercase tracking-widest opacity-80" data-testid="field-name">
          {f.field_name}
        </div>
        <ConfidenceBadge value={f.confidence} testid="field-confidence-badge" />
        {needsReview && (
          <span className="pxos-badge pxos-conf-med" data-testid="field-needs-review-badge">Da verificare</span>
        )}
        {f.edited_by_user && (
          <span className="pxos-badge" data-testid="field-edited-badge">Modificato</span>
        )}
        <span className="ml-auto text-[10px] opacity-60 font-mono">{asPct}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          value={value ?? ""}
          disabled={locked}
          onChange={(e) => { setValue(e.target.value); setDirty(true); }}
          className="flex-1 rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 disabled:opacity-70"
          style={{ borderColor: "var(--pxos-border-strong)", color: "var(--pxos-text-dark)" }}
          placeholder="—"
          data-testid="field-value-input"
        />
        <button
          disabled={locked || saving || !dirty}
          onClick={() => onSave(value === "" ? null : value)}
          className="pxos-btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
          data-testid="field-save-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva
        </button>
      </div>
    </li>
  );
}
